import { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { courseService } from '../../services/api';
import { Button, Badge, Skeleton, EmptyState, Toast } from '../../components/ui';
import { thStyle, tdStyle } from '../../utils/tableStyles';

const CourseManagement = () => {
  const { dark } = useTheme();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const response = await courseService.getAll({ limit: 200 });
      setCourses(response.data.data || []);
    } catch {
      setToast({ msg: 'Error loading courses', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      setToast({ msg: 'Syncing courses with Admission API...', type: 'info' });
      await courseService.sync();
      setToast({ msg: 'Courses synchronized successfully', type: 'success' });
      fetch();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Error syncing courses', type: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetch();
  }, [fetch]);

  const card = {
    background: dark ? '#0F172A' : '#fff',
    border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`,
    borderRadius: 16,
    overflow: 'hidden'
  };

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: 0 }}>Courses</h1>
        <div>
          <Button 
            icon={RefreshCw} 
            onClick={handleSync} 
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync Courses'}
          </Button>
        </div>
      </div>

      <div style={card}>
        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: 20 }}><Skeleton rows={5} cols={4} /></div>
          ) : courses.length === 0 ? (
            <div style={{ padding: 40 }}><EmptyState title="No courses found" /></div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                <tr>
                  <th style={thStyle(dark)}>External ID</th>
                  <th style={thStyle(dark)}>Course Name</th>
                  <th style={thStyle(dark)}>Course Code</th>
                  <th style={thStyle(dark)}>Status</th>
                </tr>
              </thead>
              <tbody>
                {courses.map(c => (
                  <tr key={c._id} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                    <td style={{ ...tdStyle(dark), fontFamily: 'monospace', fontSize: 12 }}>{c.id}</td>
                    <td style={{ ...tdStyle(dark), fontWeight: 600 }}>{c.name}</td>
                    <td style={{ ...tdStyle(dark), fontFamily: 'monospace', fontSize: 11 }}>{c.coursecode || '—'}</td>
                    <td style={tdStyle(dark)}>
                      {c.status === 1 ? (
                        <Badge color="#10B981">Enabled</Badge>
                      ) : (
                        <Badge color="#EF4444">Disabled</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseManagement;
